/**
 * Deterministic value noise. No Math.random anywhere in the voyage layer: the sea's
 * shape has to be identical in every session, on every machine, for an absent player
 * and a present one alike.
 *
 * Integer maths is done with Math.imul, which is exact int32 in every engine, so the
 * lattice hash is bit-identical across browsers rather than merely similar.
 */

import { smoothstep } from './units';

/** Hash of an integer lattice cell to [0, 1). Pure, no state. */
export function hashLattice(ix: number, iy: number, seed: number): number {
  let h = Math.imul(ix | 0, 374761393) ^ Math.imul(iy | 0, 668265263) ^ Math.imul(seed | 0, 1442695041);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/**
 * Smoothstep-interpolated value noise in [0, 1]. C1-continuous, therefore Lipschitz,
 * which is what keeps the region blend from producing a visible seam.
 */
export function valueNoise2(x: number, y: number, seed: number): number {
  const fx = Math.floor(x);
  const fy = Math.floor(y);
  const tx = smoothstep(x - fx);
  const ty = smoothstep(y - fy);

  const n00 = hashLattice(fx, fy, seed);
  const n10 = hashLattice(fx + 1, fy, seed);
  const n01 = hashLattice(fx, fy + 1, seed);
  const n11 = hashLattice(fx + 1, fy + 1, seed);

  const a = n00 + (n10 - n00) * tx;
  const b = n01 + (n11 - n01) * tx;
  return a + (b - a) * ty;
}

/** Two octaves, normalised to [0, 1]. Enough shape without a second lattice period. */
export function fieldNoise(x: number, y: number, periodLeagues: number, seed: number): number {
  const base = valueNoise2(x / periodLeagues, y / periodLeagues, seed);
  const fine = valueNoise2((x * 2.17) / periodLeagues, (y * 2.17) / periodLeagues, seed ^ 0x5bf03635);
  return (base * 2 + fine) / 3;
}
